import type {
  PortalNotificationAcknowledgement,
  PortalNotificationFeedResponse,
} from '@/lib/api/client-portal.api'
import { clientPortalApi } from '@/lib/api/client-portal.api'

import { isValidUUID } from '@scrambl/shared'

import {
  assertNotificationCount,
  NotificationReadContractError,
} from './reads.api'

export interface PortalNotificationContext {
  userId: string
  entityId: string
  organizationId: string | null
  signal: AbortSignal
}
const fail = () => {
  throw new NotificationReadContractError()
}
function assertContext(context: PortalNotificationContext) {
  context.signal.throwIfAborted()
  if (![context.userId, context.entityId].every(isValidUUID)) fail()
}
function assertIdentity(
  body: { userId: string; organizationId: string; entityId: string },
  context: PortalNotificationContext,
) {
  if (
    !body ||
    body.userId !== context.userId ||
    body.entityId !== context.entityId ||
    !isValidUUID(body.organizationId) ||
    (context.organizationId && body.organizationId !== context.organizationId)
  )
    fail()
}
function assertWindow(limit: number, offset: number) {
  if (
    !Number.isSafeInteger(limit) ||
    limit < 1 ||
    limit > 50 ||
    !Number.isSafeInteger(offset) ||
    offset < 0
  )
    fail()
}
const date = (value: unknown) =>
  typeof value === 'string' && Number.isFinite(Date.parse(value))
export function assertPortalNotificationFeed(
  body: PortalNotificationFeedResponse,
  context: PortalNotificationContext,
  limit: number,
  offset: number,
) {
  assertIdentity(body, context)
  if (
    !Array.isArray(body.items) ||
    typeof body.isInternalViewer !== 'boolean' ||
    typeof body.hasMore !== 'boolean' ||
    body.limit !== limit ||
    body.offset !== offset
  )
    fail()
  assertNotificationCount(body.unreadCount)
  assertNotificationCount(body.totalCount)
  if (
    body.unreadCount > body.totalCount ||
    body.items.length > limit ||
    body.items.length > body.totalCount ||
    body.hasMore !== offset + limit < body.totalCount
  )
    fail()
  const ids = new Set<string>()
  for (const item of body.items) {
    if (
      !item ||
      !isValidUUID(item.id) ||
      ids.has(item.id) ||
      typeof item.title !== 'string' ||
      typeof item.type !== 'string' ||
      typeof item.priority !== 'string' ||
      !date(item.occurredAt) ||
      (item.readAt != null && !date(item.readAt)) ||
      (item.message != null && typeof item.message !== 'string') ||
      typeof item.href !== 'string'
    )
      fail()
    const prefix = `/client-portal/${context.entityId}/`
    if (
      !item.href.startsWith(prefix) ||
      !new URL(item.href, 'https://portal.invalid').pathname.startsWith(prefix)
    )
      fail()
    if (
      item.actor &&
      (!isValidUUID(item.actor.id) ||
        typeof item.actor.name !== 'string' ||
        (item.actor.avatarUrl != null &&
          typeof item.actor.avatarUrl !== 'string'))
    )
      fail()
    ids.add(item.id)
  }
}
function assertAcknowledgement(
  body: PortalNotificationAcknowledgement,
  context: PortalNotificationContext,
  notificationId?: string,
) {
  assertIdentity(body, context)
  assertNotificationCount(body.updated)
  if (
    body.notificationId !== notificationId ||
    (notificationId && body.updated > 1)
  )
    fail()
}
export const portalNotificationsApi = {
  async read(context: PortalNotificationContext, limit: number, offset = 0) {
    assertContext(context)
    assertWindow(limit, offset)
    const body = await clientPortalApi.getNotificationFeed(
      context.entityId,
      limit,
      offset,
      { signal: context.signal },
    )
    assertContext(context)
    assertPortalNotificationFeed(body, context, limit, offset)
    return body
  },
  async change(
    context: PortalNotificationContext,
    action: 'read' | 'dismiss' | 'read-all',
    notificationId?: string,
  ) {
    assertContext(context)
    if (
      !['read', 'dismiss', 'read-all'].includes(action) ||
      (action === 'read-all'
        ? notificationId !== undefined
        : !notificationId || !isValidUUID(notificationId))
    )
      fail()
    const body =
      action === 'read-all'
        ? await clientPortalApi.markAllNotificationsRead(context.entityId, {
            signal: context.signal,
          })
        : await clientPortalApi.changePortalNotificationState(
            context.entityId,
            notificationId!,
            action,
            { signal: context.signal },
          )
    assertContext(context)
    assertAcknowledgement(body, context, notificationId)
    return { updated: body.updated }
  },
}
